/**
 * FO-136 (batch-27) — Directe set-/los-uitgifte + monitoring + portal-terugkoppeling.
 *
 * Gedeelde Zod-request-schemas (API `.parse`/`.safeParse` + form `.safeParse`) en
 * DTO's voor: directe set-uitgifte van een gereedschapspakket, de uitgifte-batch,
 * het retourplicht-/overdue-overzicht, de per-artikel "wie heeft de exemplaren"-blik
 * en het portal-scherm "Mijn bedrijfsmiddelen" (ontvangst bevestigen + retour melden).
 *
 * Bouwt op de bestaande uniek-item-engine + pakket-resolver + reserverings-/
 * bestelvoorstel-flow; deze module voegt alleen de directe ingang + de lichte
 * batch-groepering + de portal-terugkoppeling toe. GEEN kostprijs/marge in de
 * portal-gerichte DTO's (`Mijn*`).
 *
 * ID-referenties: `z.string().min(1)` (prod heeft legacy UUID-ids, dus NIET
 * `.cuid()`). Datums als ISO/`yyyy-mm-dd`-string; de route parset naar Date
 * (Europe/Amsterdam op de UI-rand).
 */

import { z } from 'zod'
import type {
  GereedschapVoorraadStatus,
  MijnPakketRegelDTO,
  OvergeslagenBehoefteDTO,
} from './gereedschapspakket'

// ─────────────────────────────────────────────────────────────────────────
// Enum + labels (spiegelt de Prisma-enum GereedschapRetourStatus)
// ─────────────────────────────────────────────────────────────────────────

export const GEREEDSCHAP_RETOUR_STATUS = ['AANGEMELD', 'VERWERKT', 'INGETROKKEN'] as const
export type GereedschapRetourStatus = (typeof GEREEDSCHAP_RETOUR_STATUS)[number]

export const GEREEDSCHAP_RETOUR_STATUS_LABELS: Record<GereedschapRetourStatus, string> = {
  AANGEMELD: 'Retour gemeld',
  VERWERKT: 'Ingenomen',
  INGETROKKEN: 'Ingetrokken',
}

// ─────────────────────────────────────────────────────────────────────────
// Request-schemas
// ─────────────────────────────────────────────────────────────────────────

const idRef = z.string().min(1)
const dateString = z.string().min(1).max(40)

/** Eén regel bij een vrije-selectie set-uitgifte (override op de pakketregels). */
export const setUitgifteRegelInputSchema = z.object({
  voorraadArtikelId: idRef,
  aantal: z.number().int().min(1).max(999),
  /**
   * batch-42 (FO-373) — expliciete leverwijze-splitsing: hoeveel van `aantal`
   * uit voorraad geleverd wordt (rest gaat naar het bestelvoorstel = extern).
   * Weggelaten -> de bestaande automaat (zoveel mogelijk uit voorraad), zodat de
   * preview-berekening de default blijft. Server-side geklemd op [0, aantal] én
   * op de vrije voorraad (nooit negatief; race valt terug op extern).
   */
  aantalVoorraad: z.number().int().min(0).max(999).optional(),
  /**
   * batch-45 (FO-381 §3.3) — het "geen mutaties"-deel: alleen als bezit
   * registreren (het is al buiten het systeem om uitgereikt), zonder
   * reservering/afboeking/bestelling, mét de zichtbare markering
   * "geregistreerd zonder voorraadmutatie". Server-side geklemd op [0, aantal];
   * het restant (aantal - aantalZonderMutatie) volgt de voorraad/extern-splitsing.
   */
  aantalZonderMutatie: z.number().int().min(0).max(999).optional(),
})
export type SetUitgifteRegelInput = z.infer<typeof setUitgifteRegelInputSchema>

/**
 * Directe set-uitgifte: geef in één handeling het (voorgevulde) pakket uit aan
 * een medewerker. `pakketId` leeg -> resolver op de medewerker. `regels` leeg ->
 * de volledige pakketinhoud; gevuld -> vrije regelselectie (override).
 */
export const geefSetUitSchema = z.object({
  employeeId: idRef,
  pakketId: idRef.optional().nullable(),
  regels: z.array(setUitgifteRegelInputSchema).max(200).optional(),
  /** Optionele verwachte retourdatum (gereedschap: standaard optioneel). */
  verwachteRetour: dateString.optional().nullable(),
  notitie: z.string().trim().max(2000).optional().nullable(),
  /**
   * batch-69 samenstellen (FO-509 §B3) — een PAKKET gaat alleen compleet de deur
   * uit. Ontbreekt er iets, dan weigert de API (409 PAKKET_NIET_COMPLEET) en is de
   * weg een samenstel-opdracht (reserveren + bestellijst). Alleen magazijnbeheer
   * mag met een reden toch uitgeven wat er ligt: het tekort volgt dan na.
   */
  uitzonderingReden: z.string().trim().min(5, 'Geef een reden (minimaal 5 tekens)').max(1000).optional().nullable(),
})
export type GeefSetUitInput = z.infer<typeof geefSetUitSchema>

/** Preview: resolve pakket + beschikbaarheid per regel (nog geen mutatie). */
export const setUitgiftePreviewSchema = z.object({
  employeeId: idRef,
  pakketId: idRef.optional().nullable(),
})
export type SetUitgiftePreviewInput = z.infer<typeof setUitgiftePreviewSchema>

/** Los uitgeven van één specifiek gereedschap-exemplaar aan een medewerker. */
export const geefLosUitSchema = z.object({
  employeeId: idRef,
  voorraadUniekItemId: idRef,
  verwachteRetour: dateString.optional().nullable(),
})
export type GeefLosUitInput = z.infer<typeof geefLosUitSchema>

/**
 * batch-42 (FO-373) — directe toewijzing zonder (voldoende) voorraad: bestel het
 * artikel extern MET de medewerker als bestemming, zodat de ontvangst direct tot
 * uitgifte leidt (bestaande set-flow-koppeling).
 */
export const bestelExternVoorMedewerkerSchema = z.object({
  employeeId: idRef,
  voorraadArtikelId: idRef,
  aantal: z.number().int().min(1).max(999),
  notitie: z.string().trim().max(2000).optional().nullable(),
})
export type BestelExternVoorMedewerkerInput = z.infer<typeof bestelExternVoorMedewerkerSchema>

/** Portal: ontvangst van een set bevestigen (optioneel met handtekening). */
export const bevestigOntvangstSchema = z.object({
  /** Base64 PNG data-URL van de handtekening; optioneel. */
  handtekeningData: z.string().max(2_000_000).optional().nullable(),
})
export type BevestigOntvangstInput = z.infer<typeof bevestigOntvangstSchema>

/** Portal: retour melden van een individueel uniek item. */
export const meldRetourSchema = z.object({
  voorraadUniekItemId: idRef,
  reden: z.string().trim().max(2000).optional().nullable(),
})
export type MeldRetourInput = z.infer<typeof meldRetourSchema>

/** Backoffice: een retour-melding fysiek innemen (bevestig + innemenUniek). */
export const verwerkRetourMeldingSchema = z.object({
  locatieId: idRef,
  conditie: z.string().trim().max(2000).optional().nullable(),
})
export type VerwerkRetourMeldingInput = z.infer<typeof verwerkRetourMeldingSchema>

// ─────────────────────────────────────────────────────────────────────────
// DTO's — set-uitgifte (backoffice)
// ─────────────────────────────────────────────────────────────────────────

export interface SetUitgiftePreviewRegelDTO {
  voorraadArtikelId: string
  artikelNummer: string
  naam: string
  categorie: string
  traceerbaar: boolean
  aantalNodig: number
  vrij: number
  /** Wat direct uitgegeven wordt = min(nodig, vrij). */
  aantalUitgeven: number
  /** Wat ontbreekt en naar de aanvraag-/bestel-flow gaat. */
  tekort: number
  voorraadStatus: GereedschapVoorraadStatus
}

export interface SetUitgiftePreviewDTO {
  employeeId: string
  pakket: { id: string; naam: string; omschrijving: string | null } | null
  bepaaldVia: 'OVERRIDE' | 'BV' | 'AFDELING' | 'FUNCTIEGROEP' | null
  geenPakketReden: string | null
  regels: SetUitgiftePreviewRegelDTO[]
  aantalUitgeven: number
  aantalTekort: number
  leverancierNaam: string | null
}

export interface SetUitgifteResultaatDTO {
  uitgifteId: string
  aantalUitgegeven: number
  aantalTekort: number
  /** Bij tekort: de aangemaakte aanvraag + bestelvoorstel. */
  aanvraagId: string | null
  bestelvoorstelId: string | null
  /**
   * batch-48 (spec 97 §3.1) — deel van het tekort dat NIET besteld is omdat het
   * al in een open bestelling stond. Null = niets overgeslagen. De UI moet dit
   * tonen; anders leest "n naar bestelvoorstel" als besteld terwijl dat niet zo is.
   */
  overgeslagen: OvergeslagenBehoefteDTO | null
}

/** Beschikbaar gereedschap-exemplaar voor de los-uitgifte-picker (geen kostprijs). */
export interface BeschikbaarGereedschapDTO {
  voorraadUniekItemId: string
  artikelNaam: string
  artikelNummer: string
  interneCode: string | null
}

// ─────────────────────────────────────────────────────────────────────────
// DTO's — uitgifte-batch (monitoring + detail)
// ─────────────────────────────────────────────────────────────────────────

export interface GereedschapsUitgifteRegelDTO {
  id: string
  voorraadArtikelId: string
  artikelNummer: string
  naam: string
  categorie: string
  aantal: number
  /** Uniek exemplaar (null = bulk-regel). */
  voorraadUniekItemId: string | null
  interneCode: string | null
  /** Live status van het uniek exemplaar (null = bulk). */
  uniekStatus: string | null
  /** Uniek exemplaar staat nog op naam van de medewerker (status UITGEGEVEN). */
  nogInBezit: boolean
  /**
   * batch-45 (FO-381) — dit regel-deel is bewust zonder voorraadmutatie
   * geregistreerd; de UI toont de niet-onderdrukbare markering.
   */
  zonderMutatie: boolean
  /** Open retour-melding op dit exemplaar (AANGEMELD), anders null. */
  retourStatus: GereedschapRetourStatus | null
}

export interface GereedschapsUitgifteDTO {
  id: string
  employeeId: string
  medewerkerNaam: string
  pakketId: string | null
  pakketNaam: string | null
  bepaaldVia: string | null
  aanvraagId: string | null
  aantalUitgegeven: number
  aantalTekort: number
  /** Aantal unieke exemplaren dat nog in bezit is (nog niet ingenomen). */
  aantalOpen: number
  notitie: string | null
  bevestigdOp: string | null
  uitgereiktOp: string
  uitgereiktDoorNaam: string | null
  regels: GereedschapsUitgifteRegelDTO[]
}

// ─────────────────────────────────────────────────────────────────────────
// DTO's — monitoring (retourplicht + per-artikel houders)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Eén regel in het retourplicht-/overdue-overzicht. Een set-uitgifte wordt als
 * één `SET`-regel getoond (met de nog-openstaande stuks); losse unieke/bulk-
 * uitgiftes als `UNIEK`/`BULK`.
 */
export interface RetourplichtRowDTO {
  kind: 'UNIEK' | 'BULK' | 'SET'
  employeeId: string
  medewerkerNaam: string
  artikelId: string | null
  artikelNaam: string
  categorie: string
  aantal: number
  interneCode: string | null
  uniekItemId: string | null
  sindsAt: string
  verwachteRetour: string | null
  dagenUit: number
  /** Voorbij de (gezette) verwachte retourdatum. */
  overVerwachteRetour: boolean
  /** Voorbij de te-lang-uit-grens (dagen). */
  teLangUit: boolean
  /** Monteur heeft dit uniek item retour gemeld (open melding). */
  retourGemeld: boolean
  // Set-velden (alleen bij kind === 'SET')
  uitgifteId: string | null
  pakketNaam: string | null
  setAantalOpen: number | null
}

export interface ArtikelHouderDTO {
  uniekItemId: string
  interneCode: string | null
  status: string
  employeeId: string | null
  medewerkerNaam: string | null
  sindsAt: string | null
  verwachteRetour: string | null
  retourGemeld: boolean
}

export interface ArtikelHoudersDTO {
  artikelId: string
  artikelNummer: string
  naam: string
  categorie: string
  traceerbaar: boolean
  totaalExemplaren: number
  aantalUitgegeven: number
  aantalOpVoorraad: number
  houders: ArtikelHouderDTO[]
}

// ─────────────────────────────────────────────────────────────────────────
// DTO's — portal "Mijn bedrijfsmiddelen" (GEEN kostprijs/marge)
// ─────────────────────────────────────────────────────────────────────────

export interface MijnBedrijfsmiddelUniekDTO {
  uniekItemId: string
  artikelNaam: string
  categorie: string
  interneCode: string | null
  uitgereiktOp: string | null
  verwachteRetour: string | null
  /** Onderdeel van een set-uitgifte (batch), indien van toepassing. */
  uitgifteId: string | null
  pakketNaam: string | null
  /** Open retour-melding op dit item (AANGEMELD), anders null. */
  retourStatus: GereedschapRetourStatus | null
}

export interface MijnBedrijfsmiddelBulkDTO {
  artikelId: string
  artikelNummer: string
  naam: string
  categorie: string
  eenheid: string
  aantal: number
  laatsteUitgifte: string | null
}

export interface MijnBedrijfsmiddelenSetDTO {
  uitgifteId: string
  pakketNaam: string | null
  uitgereiktOp: string
  bevestigdOp: string | null
  aantalUitgegeven: number
  /** Aantal unieke exemplaren in deze set (traceerbaar; noemer voor "X van Y"). */
  aantalUniek: number
  /** Aantal unieke exemplaren van deze set dat nog in bezit is. */
  aantalOpen: number
}

export interface MijnBedrijfsmiddelenDTO {
  pakket: { id: string; naam: string; omschrijving: string | null } | null
  geenPakketReden: string | null
  pakketRegels: MijnPakketRegelDTO[]
  uniekeItems: MijnBedrijfsmiddelUniekDTO[]
  bulkUitgiftes: MijnBedrijfsmiddelBulkDTO[]
  sets: MijnBedrijfsmiddelenSetDTO[]
}
